import { ReactNode, useMemo } from 'react';
import { RenderIf } from 'react-rainbow-components';
import ListTile from 'components/ListTile';
import UserAvatar from 'components/UserAvatar';
import getInitials from 'data/services/string/getInitials';
import useCustomerPanel from 'hooks/useCustomerPanel';
import Calendar from 'components/icons/calendar';
import Clock from 'components/icons/clock';
import Files from 'components/icons/files';
import Info from 'components/icons/info';
import Refresh from 'components/icons/refresh';
import formatter from 'data/services/date/formatter';
import formatIntlPhoneNumber from 'data/services/phone/formatter';
import {
    formatCustomValue,
    formatIsoDate,
    formatMinor,
    getBillToCustomValue,
    getCustomValue,
    orderedBillToFields,
    orderedColumns,
} from '../helpers';
import {
    Invoice,
    InvoiceBuilderColumn,
    InvoiceBuilderCustomColumn,
    InvoiceCustomer,
    InvoiceLine,
} from '../types';
import {
    CustomerTileContainer,
    DetailSection,
    DetailsPane,
    EmptyLabel,
    ItemAmount,
    ItemCard,
    ItemDescription,
    ItemField,
    ItemFields,
    ItemMainRow,
    ItemPricing,
    ItemPricingRecord,
    ItemsList,
    MetadataContainer,
    MetadataItem,
    RecordContainer,
    RecordContent,
    RecordIcon,
    RecordLabel,
    RecordsList,
    RecordValue,
    SectionTitle,
    SummaryAmount,
    SummaryLabel,
    SummaryList,
    SummaryRow,
} from './styled';

const formatTimestamp = (value?: string) => {
    if (!value) return '';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : formatter.format(date);
};

const RecordValueContent = ({ value }: { value?: ReactNode }) => (
    <RecordValue>
        {value === undefined || value === null || value === ''
            ? <EmptyLabel>Not Provided</EmptyLabel>
            : value}
    </RecordValue>
);

const Record = ({
    icon, label, value,
}: { icon: ReactNode; label: ReactNode; value?: ReactNode }) => (
    <RecordContainer>
        <RecordIcon>{icon}</RecordIcon>
        <RecordContent>
            <RecordLabel>{label}</RecordLabel>
            <RecordValueContent value={value} />
        </RecordContent>
    </RecordContainer>
);

const isCustomColumn = (
    column: InvoiceBuilderColumn,
): column is InvoiceBuilderCustomColumn => column.kind === 'custom';

const columnLabel = (
    columns: InvoiceBuilderColumn[],
    kind: InvoiceBuilderColumn['kind'],
    fallback: string,
) => columns.find((column) => column.kind === kind)?.label || fallback;

const BillToCustomer = ({ customer }: { customer?: InvoiceCustomer }) => {
    const { openCustomerPanel } = useCustomerPanel();
    const displayName = customer?.name || 'Unknown';
    const initials = useMemo(
        () => (customer?.name ? getInitials(customer.name) : null),
        [customer?.name],
    );
    if (!customer?.id && !customer?.name) return <EmptyLabel>Not Provided</EmptyLabel>;
    return (
        <ListTile
            icon={<UserAvatar initials={initials} value={customer.id} />}
            onClick={customer.id
                ? () => openCustomerPanel({ data: { id: customer.id } })
                : undefined}
            title={displayName}
            subtitle={formatIntlPhoneNumber(customer.phone) || customer.email}
        />
    );
};

interface InvoiceItemProps {
    line: InvoiceLine;
    columns: InvoiceBuilderColumn[];
    currency: string;
    locale: string;
}

const InvoiceItem = ({
    line, columns, currency, locale,
}: InvoiceItemProps) => {
    const customFields = columns.filter(isCustomColumn);
    const quantity = String(line.quantity || '0');
    return (
        <ItemCard>
            <ItemMainRow>
                <ItemDescription>
                    {line.description || <EmptyLabel>No description</EmptyLabel>}
                </ItemDescription>
                <ItemPricing>
                    <ItemPricingRecord>
                        <RecordLabel>{columnLabel(columns, 'quantity', 'Quantity')}</RecordLabel>
                        <RecordValue>{quantity}</RecordValue>
                    </ItemPricingRecord>
                    <ItemPricingRecord>
                        <RecordLabel>{columnLabel(columns, 'rate', 'Rate')}</RecordLabel>
                        <RecordValue>{formatMinor(line.rateMinor, currency, locale)}</RecordValue>
                    </ItemPricingRecord>
                    <ItemPricingRecord>
                        <RecordLabel>{columnLabel(columns, 'amount', 'Amount')}</RecordLabel>
                        <ItemAmount>
                            {formatMinor(line.amounts.totalMinor, currency, locale)}
                        </ItemAmount>
                    </ItemPricingRecord>
                </ItemPricing>
            </ItemMainRow>
            <RenderIf isTrue={customFields.length > 0}>
                <ItemFields>
                    {customFields.map((column) => (
                        <ItemField key={column.id}>
                            <RecordLabel>{column.label}</RecordLabel>
                            <RecordValueContent
                                value={formatCustomValue(
                                    getCustomValue(line.customValues, column.id),
                                    column.valueKind,
                                )}
                            />
                        </ItemField>
                    ))}
                </ItemFields>
            </RenderIf>
        </ItemCard>
    );
};

interface InvoiceDetailsProps {
    invoice: Invoice;
}

const InvoiceDetails = ({ invoice }: InvoiceDetailsProps) => {
    const { document: doc } = invoice;
    const { design } = doc;
    const currency = doc.totals.currency || design.regional.currency || 'USD';
    const locale = design.regional.locale || 'en-US';
    const columns = orderedColumns(design.columns || []);
    const billToFields = orderedBillToFields(design.billToFields || []);
    const customer = invoice.customer?.name ? invoice.customer : doc.buyer;

    return (
        <DetailsPane>
            <MetadataContainer>
                <MetadataItem>
                    <Clock />
                    {`Created ${formatTimestamp(invoice.createdAt) || '—'}`}
                </MetadataItem>
                <MetadataItem>
                    <Refresh />
                    {`Updated ${formatTimestamp(invoice.updatedAt) || '—'}`}
                </MetadataItem>
            </MetadataContainer>
            <DetailSection>
                <SectionTitle>Invoice Details</SectionTitle>
                <RecordsList $columns={2}>
                    <Record
                        icon={<Files />}
                        label={design.labels.invoiceNumber || 'Invoice Number'}
                        value={invoice.number}
                    />
                    <Record
                        icon={<Calendar />}
                        label={design.labels.issuedOn || 'Issue Date'}
                        value={formatIsoDate(invoice.issuedOn, design.regional)}
                    />
                    <Record
                        icon={<Calendar />}
                        label={design.labels.dueOn || 'Due Date'}
                        value={formatIsoDate(invoice.dueOn, design.regional)}
                    />
                </RecordsList>
            </DetailSection>
            <DetailSection>
                <SectionTitle>{design.labels.billTo || 'Bill To'}</SectionTitle>
                <CustomerTileContainer>
                    <BillToCustomer customer={customer} />
                </CustomerTileContainer>
                <RecordsList $columns={2}>
                    {billToFields.map((field) => (
                        <Record
                            key={field.id}
                            icon={field.valueKind === 'date' ? <Calendar /> : <Info />}
                            label={field.label}
                            value={formatCustomValue(
                                getBillToCustomValue(doc.billToCustomValues, field.id),
                                field.valueKind,
                            )}
                        />
                    ))}
                </RecordsList>
            </DetailSection>
            <DetailSection>
                <SectionTitle>Invoice Items</SectionTitle>
                <ItemsList>
                    {doc.lines.map((line, index) => (
                        <InvoiceItem
                            key={line.id || index}
                            line={line}
                            columns={columns}
                            currency={currency}
                            locale={locale}
                        />
                    ))}
                    <RenderIf isTrue={doc.lines.length === 0}>
                        <EmptyLabel>No items</EmptyLabel>
                    </RenderIf>
                </ItemsList>
            </DetailSection>
            <DetailSection>
                <SectionTitle>Summary</SectionTitle>
                <SummaryList>
                    <SummaryRow>
                        <SummaryLabel>Subtotal</SummaryLabel>
                        <SummaryAmount>
                            {formatMinor(doc.totals.subtotalMinor, currency, locale)}
                        </SummaryAmount>
                    </SummaryRow>
                    {(doc.totals.fees || []).map((fee) => {
                        const rate = fee.calculation.type === 'percentage'
                            ? ` (${fee.calculation.rateBasisPoints / 100}%)`
                            : '';
                        return (
                            <SummaryRow key={fee.id}>
                                <SummaryLabel>{`${fee.label}${rate}`}</SummaryLabel>
                                <SummaryAmount>
                                    {formatMinor(fee.amountMinor, currency, locale)}
                                </SummaryAmount>
                            </SummaryRow>
                        );
                    })}
                    <SummaryRow $total>
                        <SummaryLabel>Total</SummaryLabel>
                        <SummaryAmount>
                            {formatMinor(doc.totals.totalMinor, currency, locale)}
                        </SummaryAmount>
                    </SummaryRow>
                </SummaryList>
            </DetailSection>
        </DetailsPane>
    );
};

export default InvoiceDetails;
